import lodashGet from 'lodash-es/get';

import { putImage } from '@rover/react-lib/src/apis/images.api';
import {
  addImage,
  deleteImage,
  get,
  getDetail,
  getImages,
  getMemberPets,
  getPetDetailsOptions,
  postOrPut,
  setImageToFirst,
} from '@rover/react-lib/src/apis/pet.api';
import petFactory from '@rover/react-lib/src/factories/petFactory';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { ImageItem } from '@rover/react-lib/src/pages/pets/EditPetProfilePage/EditPetProfilePage.duck';
import { cleanPetDetails } from '@rover/react-lib/src/pages/pets/EditPetProfilePage/utils';
import { selectors as currentCountryConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/countryConfiguration.duck';
import { selectors as personSummarySelectors } from '@rover/react-lib/src/redux/ducks/personSummary.duck';
import {
  getPetDetailsFromPet,
  getPetInformationKeys,
  getPetOpkFromUrl,
} from '@rover/react-lib/src/utils/pets';
import { kebabifyString } from '@rover/rsdk/src/modules/Network/object-utilities';
import type {
  FetchPetImageResponse,
  Image,
  Pet,
  PetDetailForDisplay,
  PetDetailsOptions,
  PetsResponse,
} from '@rover/types';
import triggerEmbeddedEvent from '@rover/utilities/embedded';

export type PetImages = {
  count: number;
  images: Image[];
  next: string | null | undefined;
};

export type State = {
  pets: Pet[];
  memorializedPets: Pet[];
  petInformation: Pet;
  petImages: PetImages;
  isLoading: boolean;
  isSaving: boolean;
  isLoadingImages: boolean;
  isDeletingImages: boolean;
  submittingCount: number;
  errorMessage: string;
  savePetErrorMessage: string;
  petDetailsOptions: PetDetailsOptions;
  isLoadingOptions: boolean;
  uploadedImageUrls: string[];
  petImageListUrl: string;
  images: ImageItem[];
};

type Actions = {
  deleteImagesPending: ActionCreator<void>;
  deleteImagesSuccess: ActionCreator<void>;
  deleteImagesFailure: ActionCreator<{
    message: string;
  }>;
  updatePetImageListUrl: ActionCreator<string>;
  addImagesPending: ActionCreator<void>;
  addImagesSuccess: ActionCreator<void>;
  addImagesFailure: ActionCreator<{
    message: string;
  }>;
  setHeroImageFailure: ActionCreator<{ message: string }>;
  fetchPetsPending: ActionCreator<void>;
  fetchPetsSuccess: ActionCreator<PetsResponse>;
  fetchPetsFailure: ActionCreator<string>;
  savePetPending: ActionCreator<void>;
  savePetSuccess: ActionCreator<Pet>;
  deletePetSuccess: ActionCreator<string>;
  updatePetList: ActionCreator<Pet>;
  savePetFailure: ActionCreator<string>;
  fetchPetInformationPending: ActionCreator<void>;
  fetchPetInformationSuccess: ActionCreator<Pet>;
  fetchPetInformationFailure: ActionCreator<string>;
  fetchPetImagesPending: ActionCreator<void>;
  fetchPetImagesSuccess: ActionCreator<FetchPetImageResponse>;
  fetchPetImagesFailure: ActionCreator<string>;
  fetchPetDetailsOptionsPending: ActionCreator<void>;
  fetchPetDetailsOptionsSuccess: ActionCreator<PetDetailsOptions>;
  fetchPetDetailsOptionsFailure: ActionCreator<string>;
  deletePetImagePending: ActionCreator<void>;
  deletePetImageSuccess: ActionCreator<string>;
  deletePetImageFailure: ActionCreator<string>;
  clearPets: ActionCreator<void>;
  clearPet: ActionCreator<void>;
  setPetInformation: ActionCreator<Pet>;
  updateHeroImage: ActionCreator<Image>;
  setState: ActionCreator<State>;
};

type Effects = {
  deleteImages: EffectCreator<void>;
  saveImages: EffectCreator<void>;
  setPetImage: EffectCreator<Image>;
  updateImagesDetails: EffectCreator<void>;
  fetchPets: EffectCreator<void>;
  fetchMemberPets: EffectCreator<string>;
  fetchPetInformation: EffectCreator<string>;
  fetchPetImages: EffectCreator<{
    imageListUrl: string;
    pageSize?: number;
  }>;
  fetchPetDetailsOptions: EffectCreator<void>;
  savePet: EffectCreator<Pet | Partial<Pet>>;
  savePetWithImages: EffectCreator<Pet | Partial<Pet>>;
  deleteImage: EffectCreator<string>;
};

type Selectors = {
  getDeleteImagesErrorMessage: SelectorCreator<string | null | undefined>;
  getUploadedImageUrls: SelectorCreator<string[]>;
  getPetImageListUrl: SelectorCreator<string>;
  getAllImages: SelectorCreator<ImageItem[]>;
  getPets: SelectorCreator<Pet[]>;
  getMemorializedPets: SelectorCreator<Pet[]>;
  getPetsCount: SelectorCreator<number>;
  getPetInformation: SelectorCreator<Pet>;
  getPetOpk: SelectorCreator<string>;
  getPetInformationBooleans: SelectorCreator<PetDetailForDisplay[]>;
  getPetDetailsInformation: SelectorCreator<PetDetailForDisplay[]>;
  isPetInfoSubmitting: SelectorCreator<boolean>;
  isPetInfoSaving: SelectorCreator<boolean>;
  getPetsError: SelectorCreator<string>;
  getSavePetErrorMessage: SelectorCreator<string>;
  getPetImagesUrl: SelectorCreator<string>;
  getPetImages: SelectorCreator<PetImages>;
  getIsLoading: SelectorCreator<boolean>;
  getIsOwnPet: SelectorCreator<boolean>;
  getIsPetOwned: SelectorCreator<boolean>;
  getPetDetailsOptions: SelectorCreator<PetDetailsOptions>;
  getPetDetailsOptionsLoading: SelectorCreator<boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

const convertWeight =
  (weightUnit) =>
  (pet: Pet): Pet => {
    let { weight } = pet;
    let unit = 'lbs';
    const weightObj = pet.weightObj.find((item) => item.unit === weightUnit);
    weight = weightObj ? parseInt(weightObj.value, 10) : pet.weight;
    unit = weightObj ? weightObj.unit : 'lbs';
    return { ...pet, weight, unit };
  };

const createInitialState = () => ({
  pets: [],
  memorializedPets: [],
  petInformation: petFactory(),
  petImages: {
    count: 0,
    images: [],
    next: null,
  },
  isLoading: false,
  isSaving: false,
  isDeletingImages: false,
  isLoadingImages: false,
  submittingCount: 0,
  errorMessage: '',
  savePetErrorMessage: '',
  petDetailsOptions: [],
  isLoadingOptions: false,
  adoptionDate: null,
  uploadedImageUrls: [],
  petImageListUrl: '',
  images: [],
});

export const options: Options = {
  name: 'pets',
  initialState: createInitialState(),
  actions: {
    deleteImagesPending: () => (state) => ({ ...state, isDeletingImages: true }),
    deleteImagesSuccess: () => (state) => ({
      ...state,
      isDeletingImages: false,
      deleteImagesErrorMessage: undefined,
    }),
    deleteImagesFailure:
      ({ message }) =>
      (state) => ({ ...state, isDeletingImages: false, deleteImagesErrorMessage: message }),
    updatePetImageListUrl: (petImageListUrl) => (state) => ({ ...state, petImageListUrl }),
    addImagesPending: () => (state) => ({ ...state, isAddingImages: true }),
    addImagesSuccess: () => (state) => ({
      ...state,
      isAddingImages: false,
      addImagesErrorMessage: undefined,
    }),
    addImagesFailure:
      ({ message }) =>
      (state) => ({ ...state, isAddingImages: false, addImagesErrorMessage: message }),
    setHeroImageFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        isSettingHeroImage: false,
        setHeroImageErrorMessage: message,
      }),
    fetchPetsPending: () => (state) => ({ ...state, isLoading: true }),
    fetchPetsSuccess:
      ({ pets, memorializedPets }) =>
      (state) => ({ ...state, pets, memorializedPets, isLoading: false, errorMessage: '' }),
    fetchPetsFailure: (errorMessage) => (state) => ({ ...state, isLoading: false, errorMessage }),
    savePetPending: () => (state) => ({
      ...state,
      submittingCount: state.submittingCount + 1,
      isSaving: true,
    }),
    savePetSuccess: (savedPet) => (state) => {
      const existingPet = state.pets.find((pet) => pet.url === savedPet.url);
      const updatedPets = existingPet
        ? state.pets.map((pet) => (pet.url === savedPet.url ? savedPet : pet))
        : [...state.pets, savedPet];
      return {
        ...state,
        submittingCount: state.submittingCount - 1,
        pets: updatedPets,
        savePetErrorMessage: '',
        isSaving: false,
      };
    },
    updatePetList: (savedPet) => (state) => {
      const existingPet = state.pets.find((pet) => pet.url === savedPet.url);
      const updatedPets = existingPet
        ? state.pets.map((pet) => (pet.url === savedPet.url ? savedPet : pet))
        : [...state.pets, savedPet];
      return { ...state, pets: updatedPets };
    },
    deletePetSuccess: (petUrl) => (state) => {
      const updatedPets = state.pets.filter((pet) => pet.url.indexOf(petUrl) < 0);
      return { ...state, pets: updatedPets };
    },
    savePetFailure: (message) => (state) => ({
      ...state,
      submittingCount: state.submittingCount - 1,
      savePetErrorMessage: message,
      isSaving: false,
    }),
    fetchPetInformationPending: () => (state) => ({ ...state, isLoading: true }),
    fetchPetInformationSuccess: (petInformation) => (state) => ({
      ...state,
      petInformation,
      isLoading: false,
      errorMessage: '',
    }),
    fetchPetInformationFailure: (errorMessage) => (state) => ({
      ...state,
      isLoading: false,
      errorMessage,
    }),
    fetchPetImagesPending: () => (state) => ({ ...state, isLoadingImages: true }),
    fetchPetImagesSuccess:
      ({ results, previous, count, next }) =>
      (state) => ({
        ...state,
        petImages: {
          count,
          // previous and next are urls that we can use to get the previous/next page of images.
          // when previous is null, it means that we are on the first page.
          // And we should clean up the existing images to avoid duplicating. Else if previous is defined,
          // we will append to existing images.
          images: previous ? [...state.petImages.images, ...results] : results,
          next,
        },
        isLoadingImages: false,
        errorMessage: '',
      }),
    fetchPetImagesFailure: (errorMessage) => (state) => ({
      ...state,
      isLoadingImages: false,
      errorMessage,
    }),
    fetchPetDetailsOptionsPending: () => (state) => ({ ...state, isLoadingOptions: true }),
    fetchPetDetailsOptionsSuccess: (petDetailsOptions) => (state) => ({
      ...state,
      petDetailsOptions,
      isLoadingOptions: false,
      errorMessage: '',
    }),
    fetchPetDetailsOptionsFailure: (errorMessage) => (state) => ({
      ...state,
      isLoadingOptions: false,
      errorMessage,
    }),
    deletePetImagePending: () => (state) => ({ ...state, isDeletingImages: true }),
    deletePetImageSuccess: (imageUrl) => (state) => ({
      ...state,
      petInformation:
        state.petInformation.defaultImage.url !== imageUrl
          ? state.petInformation
          : {
              ...state.petInformation,
              defaultImage: state.petImages.images[1] || petFactory().defaultImage,
            },
      petImages: {
        count: state.petImages.count - 1,
        images: state.petImages.images.filter((image) => image.url !== imageUrl),
        next: state.petImages.next,
      },
      isDeletingImages: false,
      errorMessage: '',
    }),
    deletePetImageFailure: (errorMessage) => (state) => ({
      ...state,
      isDeletingImages: false,
      errorMessage,
    }),
    clearPets: () => (state) => {
      const emptyState = createInitialState();
      return {
        ...state,
        pets: emptyState.pets,
        petInformation: emptyState.petInformation,
        petImages: emptyState.petImages,
      };
    },
    clearPet: () => (state) => {
      const emptyState = createInitialState();
      return {
        ...state,
        petInformation: emptyState.petInformation,
        petImages: emptyState.petImages,
      };
    },
    setPetInformation: (petInformation: Pet) => (state) => ({ ...state, petInformation }),
    updateHeroImage: (image: Image) => (state) => ({
      ...state,
      petInformation: { ...state.petInformation, defaultImage: image },
    }),
    setState: (persistedState: any) => (state) => ({ ...state, ...persistedState }),
  },
  effects: {
    fetchPets:
      () =>
      async (dispatch, getState, { fetchPetsPending, fetchPetsSuccess, fetchPetsFailure }) => {
        dispatch(fetchPetsPending());

        try {
          const fetchPetsResponse: PetsResponse = await get();
          dispatch(fetchPetsSuccess(fetchPetsResponse));
        } catch (e) {
          dispatch(fetchPetsFailure((e as Error).message));
        }
      },
    fetchMemberPets:
      (memberUrl) =>
      // @ts-expect-error
      async (
        dispatch,
        getState,
        { fetchPetsPending, fetchPetsSuccess, fetchPetsFailure }
      ): Promise<string | null | undefined> => {
        dispatch(fetchPetsPending());

        try {
          const { pets, memorializedPets, memberName } = await getMemberPets(memberUrl);
          dispatch(fetchPetsSuccess({ pets, memorializedPets }));
          return memberName;
        } catch (e) {
          dispatch(fetchPetsFailure((e as Error).message));
          return undefined;
        }
      },
    fetchPetInformation:
      (petUrl) =>
      async (
        dispatch,
        getState,
        { fetchPetInformationPending, fetchPetInformationSuccess, fetchPetInformationFailure }
      ) => {
        dispatch(fetchPetInformationPending());

        try {
          dispatch(fetchPetInformationSuccess(await getDetail(petUrl)));
        } catch (e) {
          dispatch(fetchPetInformationFailure((e as Error).message));
        }
      },
    fetchPetImages:
      ({ imageListUrl, pageSize }) =>
      async (
        dispatch,
        getState,
        { fetchPetImagesPending, fetchPetImagesSuccess, fetchPetImagesFailure }
      ) => {
        dispatch(fetchPetImagesPending());

        try {
          dispatch(fetchPetImagesSuccess(await getImages(imageListUrl, pageSize)));
        } catch (e) {
          dispatch(fetchPetImagesFailure((e as Error).message));
        }
      },
    fetchPetDetailsOptions:
      () =>
      async (
        dispatch,
        getState,
        {
          fetchPetDetailsOptionsPending,
          fetchPetDetailsOptionsSuccess,
          fetchPetDetailsOptionsFailure,
        }
      ) => {
        dispatch(fetchPetDetailsOptionsPending());

        try {
          dispatch(fetchPetDetailsOptionsSuccess(await getPetDetailsOptions()));
        } catch (e) {
          dispatch(fetchPetDetailsOptionsFailure((e as Error).message));
        }
      },
    deleteImage:
      (imageUrl) =>
      async (
        dispatch,
        getState,
        { deletePetImagePending, deletePetImageSuccess, deletePetImageFailure }
      ) => {
        dispatch(deletePetImagePending());

        try {
          await deleteImage(imageUrl);
          dispatch(deletePetImageSuccess(imageUrl));
        } catch (e) {
          dispatch(deletePetImageFailure((e as Error).message));
        }
      },
    // @ts-expect-error
    savePet:
      (pet: Pet) =>
      async (dispatch, getState, actions): Promise<Pet | null | undefined> => {
        dispatch(actions.savePetPending());
        try {
          const savedPet: Pet = await postOrPut(pet);
          dispatch(actions.savePetSuccess(savedPet));
          // for embedded web, indicate that we need to refresh the pet profile
          triggerEmbeddedEvent('pet-added-modified');
          return savedPet;
        } catch (e) {
          dispatch(actions.savePetFailure((e as Error).message));
        }
        return null;
      },
    // @ts-expect-error
    savePetWithImages:
      (pet: Pet) =>
      async (
        dispatch,
        getState,
        actions,
        { saveImages, deleteImages, setPetImage, updateImagesDetails },
        { getDeleteImagesErrorMessage }
      ): Promise<Pet | null | undefined> => {
        dispatch(actions.savePetPending());

        try {
          const savedPet: Pet = await postOrPut(pet);
          // for embedded web, indicate that we need to refresh the pet profile
          triggerEmbeddedEvent('pet-added-modified');

          dispatch(actions.updatePetImageListUrl(savedPet?.urls?.images));
          await dispatch(deleteImages());
          const deleteErrorMessage = getDeleteImagesErrorMessage(getState());

          if (deleteErrorMessage) {
            throw new Error(deleteErrorMessage);
          }

          // update available images
          await dispatch(updateImagesDetails());
          await dispatch(saveImages());
          const cleanedPet = cleanPetDetails(pet);
          const petImage = cleanedPet.defaultImage;
          // upload hero
          if (petImage.shouldBeUpdatedAsHero) {
            await dispatch(setPetImage(petImage));
          }

          // update pet in list
          const updatedPet = await getDetail(savedPet.url);
          dispatch(actions.savePetSuccess(updatedPet));

          return updatedPet;
        } catch (e) {
          dispatch(actions.savePetFailure((e as Error).message));
        }

        return null;
      },
    deleteImages:
      () =>
      async (dispatch, getState, duckActions, effects, { getAllImages }): Promise<boolean> => {
        dispatch(duckActions.deleteImagesPending());

        try {
          let imageItems = getAllImages(getState());

          if (imageItems) {
            // call delete api only for those images who are not in memory-only
            imageItems = imageItems.filter((img) => img.isDeleted === true && img.image.urls);
          }

          let attemptedDelete = false;
          await Promise.all(
            imageItems.map(async (imageItem) => {
              attemptedDelete = true;
              await deleteImage(imageItem.image.url);
            })
          );
          dispatch(duckActions.deleteImagesSuccess());
          return attemptedDelete;
        } catch (error) {
          dispatch(duckActions.deleteImagesFailure(error as Error));
          return false;
        }
      },
    saveImages:
      () =>
      /* Due to this effect returning a value, the typechecker is difficult to satisfy.
      The refactor is out of scope for this TS conversion ticket */
      // @ts-expect-error
      async (
        dispatch,
        getState,
        duckActions,
        effects,
        { getUploadedImageUrls, getPetImageListUrl, getAllImages }
      ): Promise<Image[]> => {
        dispatch(duckActions.addImagesPending());

        try {
          const allImages = getAllImages(getState());
          const allDeletedImageUrls = allImages.map((i) => i.isDeleted && i.image.url);
          const uploadedImagesUrls = getUploadedImageUrls(getState());
          const petImageListUrl = getPetImageListUrl(getState());
          // filter only uploaded images that were not removed meanwhile
          const images = allImages.filter(
            (i) =>
              uploadedImagesUrls.includes(i.image.url) && !allDeletedImageUrls.includes(i.image.url)
          );
          const addedImages = await Promise.all(
            images.map((i) => addImage(petImageListUrl, i.image.url, i.image.caption))
          );
          dispatch(duckActions.addImagesSuccess());
          return addedImages;
        } catch (error) {
          dispatch(duckActions.addImagesFailure(error as Error));
          return [];
        }
      },
    setPetImage:
      (image) =>
      async (dispatch, getState, duckActions, effects, { getPetImageListUrl }) => {
        try {
          const { url } = image;
          const petImageListUrl = getPetImageListUrl(getState());
          const savedHero = await addImage(petImageListUrl, url);
          await setImageToFirst(savedHero.urls.ordering);
        } catch (error) {
          dispatch(duckActions.setHeroImageFailure(error as Error));
        }
      },
    updateImagesDetails:
      () =>
      async (
        dispatch,
        getState,
        { addImagesPending, addImagesSuccess, addImagesFailure },
        duckEffects,
        duckSelectors
      ) => {
        dispatch(addImagesPending());

        try {
          const images = duckSelectors.getAllImages(getState());
          // update all images marked for update
          await Promise.all(
            images.map(async (item) => {
              if (!item.isDeleted && item.isUpdated && item.image && item.image.added) {
                await putImage(item.image);
              }
            })
          );
          dispatch(addImagesSuccess());
        } catch (err) {
          dispatch(addImagesFailure(err as Error));
        }
      },
  },
  selectors: {
    getDeleteImagesErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.deleteImagesErrorMessage),
    getUploadedImageUrls: (getState, createSelector) =>
      createSelector([getState], (s) => s.uploadedImageUrls),
    getPetImageListUrl: (getState, createSelector) =>
      createSelector([getState], (s) => s.petImageListUrl),
    getAllImages: (getState, createSelector) => createSelector([getState], (s) => s.images),
    getPets: (getState, createSelector) =>
      createSelector(
        [getState, currentCountryConfigurationSelectors.getWeightLabel],
        (state, weightUnit) => state.pets.map(convertWeight(weightUnit))
      ),
    getMemorializedPets: (getState, createSelector) =>
      createSelector(
        [getState, currentCountryConfigurationSelectors.getWeightLabel],
        (state, weightUnit) => state.memorializedPets.map(convertWeight(weightUnit))
      ),
    getPetsCount: (getState, createSelector, { getPets }) =>
      createSelector([getState, getPets], (state, pets) => (Array.isArray(pets) ? pets.length : 0)),
    getPetInformation: (getState, createSelector) =>
      createSelector(
        [getState, currentCountryConfigurationSelectors.getWeightLabel],
        (state, weightUnit) => convertWeight(weightUnit)(state.petInformation)
      ),
    getPetOpk: (getState, createSelector, { getPetInformation }) =>
      createSelector([getState, getPetInformation], (state, pet) => getPetOpkFromUrl(pet.url)),
    getPetImagesUrl: (getState, createSelector, { getPetInformation }) =>
      createSelector([getPetInformation], (pet) => lodashGet(pet, 'urls.images')),
    getPetImages: (getState, createSelector) =>
      createSelector([getState], (state) => state.petImages),
    getIsOwnPet: (getState, createSelector, { getPetInformation }) =>
      createSelector(
        [getPetInformation, personSummarySelectors.getPersonUrl],
        (pet, personUrl) => pet.owner === personUrl
      ),
    // check if the displayed pet is in the displayed pets list
    getIsPetOwned: (getState, createSelector, { getPetInformation, getPets }) =>
      createSelector([getPetInformation, getPets], (displayedPet, pets) =>
        pets.length ? pets.map((pet) => pet.pk).includes(displayedPet.pk) : true
      ),
    getPetInformationBooleans: (getState, createSelector, { getPetInformation }) =>
      createSelector([getState, getPetInformation], (state, pet) => {
        const petInfoKeys = getPetInformationKeys();
        const petDetails = getPetDetailsFromPet(pet);
        return petDetails.filter((pd) =>
          petInfoKeys.some((infoKey) => kebabifyString(infoKey.key) === pd.slug)
        );
      }),
    getPetDetailsInformation: (
      getState,
      createSelector,
      { getPetInformation, getPetInformationBooleans }
    ) =>
      createSelector(
        [getState, getPetInformation, getPetInformationBooleans],
        (state, pet, petOldModel) => {
          // note: this selector is used to merge old model attributes for pet details
          // with new model attributes with pet detail
          const { petDetails } = pet;
          return [...petOldModel, ...(petDetails || [])];
        }
      ),
    isPetInfoSubmitting: (getState, createSelector) =>
      createSelector([getState], (s) => s.submittingCount > 0),
    isPetInfoSaving: (getState, createSelector) => createSelector([getState], (s) => s.isSaving),
    getPetsError: (getState, createSelector) => createSelector([getState], (s) => s.errorMessage),
    getSavePetErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.savePetErrorMessage),
    getIsLoading: (getState, createSelector) => createSelector([getState], (s) => s.isLoading),
    getPetDetailsOptions: (getState, createSelector) =>
      createSelector([getState], (s) => s.petDetailsOptions),
    getPetDetailsOptionsLoading: (getState, createSelector) =>
      createSelector([getState], (s) => s.isLoadingOptions),
    // getPetAdoptionDate: (getState, create)
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
